import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, AlertTriangle, RotateCw } from 'lucide-react';
import ENTITY_ICONS from './common/entityIcons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import DataTable, { RowActions, RowLink } from './common/DataTable';
import StatusBadge from './common/StatusBadge';
import Modal from './common/Modal';
import useClientTable from '../hooks/useClientTable';
import dashboardService from '../services/dashboard';

const isActive = (v) => v === 1 || v === '1' || v === true;

const toTime = (v) => {
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
};

// "Oct 26, 2026, 12:33 PM" — kept on one line by the cell's whitespace-nowrap.
const formatDate = (dateStr) => {
    const t = toTime(dateStr);
    if (t == null) return dateStr ? String(dateStr) : 'N/A';
    return new Date(t).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const formatDateOnly = (dateStr) => {
    const t = toTime(dateStr);
    if (t == null) return dateStr ? String(dateStr) : 'N/A';
    return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const USER_LIMIT = 100;

const DATE_KEYS = new Set(['last_activity', 'created_on']);
const sortValue = (row, key) => (DATE_KEYS.has(key) ? toTime(row[key]) : row[key]);

const qs = (params) =>
    new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();

const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
        headers.join(','),
        ...data.map((row) => headers.map((header) => JSON.stringify(row[header] || '')).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// --- shared cell renderers -------------------------------------------------

const num = (key) => ({
    key,
    sortKey: key,
    align: 'right',
    className: 'w-px whitespace-nowrap tabular-nums',
    headerClassName: 'w-px',
    render: (row) => Number(row[key] || 0).toLocaleString(),
});

const dateCol = (key, header, fmt = formatDateOnly) => ({
    key,
    header,
    sortKey: key,
    className: 'w-px whitespace-nowrap text-slate-600',
    render: (row) => fmt(row[key]),
});

const statusCol = {
    key: 'is_active',
    header: 'Status',
    sortKey: 'is_active',
    className: 'w-px whitespace-nowrap',
    render: (row) => <StatusBadge active={isActive(row.is_active)} />,
};

const linkClass =
    'block truncate font-medium text-slate-900 hover:text-primary-700 hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200';

/** Truncated primary-cell link with full text on hover. */
const CellLink = ({ to, text, label, maxW = 'max-w-[16rem]' }) => (
    <Link to={to} title={label || text} className={`${linkClass} ${maxW}`}>
        {text || '—'}
    </Link>
);

const Muted = ({ text, maxW = 'max-w-[14rem]' }) => (
    <span className={`block truncate text-slate-600 ${maxW}`} title={text || ''}>
        {text || '—'}
    </span>
);

// --- user detail modal -----------------------------------------------------

// No existing endpoint lists a user's memberships across apps (GET /api/user/get-user-details/:username
// is scoped to one app_id + tenant), so the modal summarises the report row and links to the Users page
// search instead of listing per-app links.
const UserDetailModal = ({ user, onClose }) => {
    if (!user) return null;
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;
    const fields = [
        ['Username', user.username],
        ['Email', user.email_id],
        ['Applications', Number(user.app_count || 0).toLocaleString()],
        ['Role assignments', Number(user.role_count || 0).toLocaleString()],
        ['Last activity', formatDate(user.last_activity)],
        ['Created', formatDateOnly(user.created_on)],
    ];
    return (
        <Modal isOpen onClose={onClose} title={fullName} size="sm">
            <div className="space-y-4">
                <StatusBadge active={isActive(user.is_active)} />
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                    {fields.map(([label, value]) => (
                        <React.Fragment key={label}>
                            <dt className="text-slate-500 whitespace-nowrap">{label}</dt>
                            <dd className="text-slate-900 truncate" title={value || ''}>
                                {value || '—'}
                            </dd>
                        </React.Fragment>
                    ))}
                </dl>
                <p className="text-xs text-slate-500">
                    Per-application memberships are not included in this report. Open the Users page to see this user's
                    apps and roles.
                </p>
                <Link
                    to={`/users?${qs({ search: user.username })}`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                >
                    <ExternalLink className="h-4 w-4" />
                    Find in Users
                </Link>
            </div>
        </Modal>
    );
};

// --- main component --------------------------------------------------------

const Reports = () => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState({
        userActivity: [],
        appUsage: [],
        roleAssignment: [],
        tenantDistribution: [],
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [error, setError] = useState(false);
    const [view, setView] = useState('users');

    const loadReports = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await dashboardService.getReports('all', USER_LIMIT);
            if (!data) throw new Error('Empty reports response');
            setReports({
                userActivity: data.userActivity || [],
                appUsage: data.appUsage || [],
                roleAssignment: data.roleAssignment || [],
                tenantDistribution: data.tenantDistribution || [],
            });
        } catch (err) {
            console.error('Error loading reports:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    // One hook per table so each keeps its own search/sort/page when switching views.
    const userTable = useClientTable(reports.userActivity, {
        searchKeys: ['username', 'email_id', 'first_name', 'last_name'],
        sortValue,
    });
    const appTable = useClientTable(reports.appUsage, { searchKeys: ['app_name', 'app_id'], sortValue });
    const roleTable = useClientTable(reports.roleAssignment, { searchKeys: ['role_name', 'app_name'], sortValue });
    const tenantTable = useClientTable(reports.tenantDistribution, {
        searchKeys: ['tenant_name', 'app_name', 'tenant_id'],
        sortValue,
    });

    const userColumns = [
        {
            key: 'user',
            header: 'User',
            sortKey: 'username',
            className: 'max-w-[18rem]',
            render: (u) => {
                const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
                return (
                    <button
                        type="button"
                        onClick={() => setSelectedUser(u)}
                        title={`View ${u.username}`}
                        className="block w-full max-w-[16rem] text-left rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 group"
                    >
                        <span className="block truncate font-medium text-slate-900 group-hover:text-primary-700 group-hover:underline">
                            {name || u.username}
                        </span>
                        {name && <span className="block truncate text-xs text-slate-500">{u.username}</span>}
                    </button>
                );
            },
        },
        {
            key: 'email_id',
            header: 'Email',
            sortKey: 'email_id',
            render: (u) => <Muted text={u.email_id} maxW="max-w-[16rem]" />,
        },
        { ...num('app_count'), header: 'Apps' },
        { ...num('role_count'), header: 'Roles' },
        dateCol('last_activity', 'Last membership change', formatDate),
        statusCol,
        dateCol('created_on', 'Created'),
    ];

    const appColumns = [
        {
            key: 'app_name',
            header: 'Application',
            sortKey: 'app_name',
            render: (a) => (
                <CellLink
                    to={`/users?${qs({ app_id: a.app_id })}`}
                    text={a.app_name}
                    label={`View users in ${a.app_name}`}
                />
            ),
        },
        { ...num('total_users'), header: 'Active users' },
        { ...num('total_roles'), header: 'Roles' },
        { ...num('total_tenants'), header: 'Tenants' },
        { ...num('total_components'), header: 'Components' },
        { ...num('new_users_30d'), header: 'New (30d)' },
        statusCol,
        dateCol('created_on', 'Created'),
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            className: 'w-px whitespace-nowrap',
            render: (a) => {
                const q = qs({ app_id: a.app_id });
                return (
                    <RowActions>
                        <RowLink icon={ENTITY_ICONS.users} label={`Users in ${a.app_name}`} to={`/users?${q}`} />
                        <RowLink icon={ENTITY_ICONS.roles} label={`Roles in ${a.app_name}`} to={`/roles?${q}`} />
                        <RowLink
                            icon={ENTITY_ICONS.components}
                            label={`Components in ${a.app_name}`}
                            to={`/components?${q}`}
                        />
                        {Number(a.total_tenants) > 0 && (
                            <RowLink
                                icon={ENTITY_ICONS.tenants}
                                label={`Tenants in ${a.app_name}`}
                                to={`/tenants?${q}`}
                            />
                        )}
                    </RowActions>
                );
            },
        },
    ];

    const roleColumns = [
        {
            key: 'role_name',
            header: 'Role',
            sortKey: 'role_name',
            render: (r) => (
                <CellLink
                    to={`/roles?${qs({ app_id: r.app_id, search: r.role_name })}`}
                    text={r.role_name}
                    label={`Open role ${r.role_name} in ${r.app_name}`}
                />
            ),
        },
        { key: 'app_name', header: 'Application', sortKey: 'app_name', render: (r) => <Muted text={r.app_name} /> },
        { ...num('assigned_users'), header: 'Assigned' },
        { ...num('new_assignments_30d'), header: 'New (30d)' },
        statusCol,
        dateCol('created_on', 'Created'),
    ];

    const tenantColumns = [
        {
            key: 'tenant_name',
            header: 'Tenant',
            sortKey: 'tenant_name',
            render: (t) => (
                <CellLink
                    to={`/users?${qs({ app_id: t.app_id, tenant_id: t.tenant_id })}`}
                    text={t.tenant_name}
                    label={`View users in ${t.tenant_name}`}
                />
            ),
        },
        { key: 'app_name', header: 'Application', sortKey: 'app_name', render: (t) => <Muted text={t.app_name} /> },
        { ...num('user_count'), header: 'Users' },
        { ...num('role_count'), header: 'Roles' },
        statusCol,
        dateCol('created_on', 'Created'),
    ];

    const views = [
        {
            id: 'users',
            label: 'Users',
            icon: ENTITY_ICONS.users,
            title: 'User accounts',
            description:
                reports.userActivity.length >= USER_LIMIT
                    ? `The ${USER_LIMIT} accounts whose app membership changed most recently. Select a user for details.`
                    : 'Every account with its app and role counts. Select a user for details.',
            data: reports.userActivity,
            filename: 'user-accounts.csv',
            table: userTable,
            columns: userColumns,
            rowKey: (u) => u.username,
            searchPlaceholder: 'Search users…',
            emptyMessage: 'No user accounts yet.',
        },
        {
            id: 'apps',
            label: 'Apps',
            icon: ENTITY_ICONS.apps,
            title: 'Applications',
            description: 'Active members, roles, tenants and components per app. Select an app to view its users.',
            data: reports.appUsage,
            filename: 'applications.csv',
            table: appTable,
            columns: appColumns,
            rowKey: (a) => a.app_id,
            searchPlaceholder: 'Search applications…',
            emptyMessage: 'No applications yet.',
        },
        {
            id: 'roles',
            label: 'Roles',
            icon: ENTITY_ICONS.roles,
            title: 'Role assignments',
            description: 'Active roles and how many users hold each. Select a role to open it.',
            data: reports.roleAssignment,
            filename: 'role-assignments.csv',
            table: roleTable,
            columns: roleColumns,
            rowKey: (r) => `${r.app_id}:${r.role_id}`,
            searchPlaceholder: 'Search roles or apps…',
            emptyMessage: 'No active roles yet.',
        },
        {
            id: 'tenants',
            label: 'Tenants',
            icon: ENTITY_ICONS.tenants,
            title: 'Tenants',
            description: 'Active tenants with their member and role counts. Select a tenant to view its users.',
            data: reports.tenantDistribution,
            filename: 'tenants.csv',
            table: tenantTable,
            columns: tenantColumns,
            rowKey: (t) => `${t.app_id}:${t.tenant_id}`,
            searchPlaceholder: 'Search tenants or apps…',
            emptyMessage: 'No active tenants yet.',
        },
    ];
    const current = views.find((v) => v.id === view);
    const Icon = current.icon;

    return (
        <Card>
            <CardHeader className="pb-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2">
                            <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                            {current.title}
                        </CardTitle>
                        <CardDescription>{current.description}</CardDescription>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportToCSV(current.data, current.filename)}
                        disabled={loading || error || current.data.length === 0}
                        title="Exports every row, not just the current page or search"
                    >
                        <Download className="h-4 w-4" />
                        Export CSV
                    </Button>
                </div>
                <div
                    role="group"
                    aria-label="Table to show"
                    className="mt-3 inline-flex max-w-full flex-wrap gap-1 rounded-lg bg-slate-100 p-1"
                >
                    {views.map((v) => (
                        <button
                            key={v.id}
                            type="button"
                            aria-pressed={v.id === view}
                            onClick={() => setView(v.id)}
                            className={`rounded-md border px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 ${
                                v.id === view
                                    ? 'border-slate-200 bg-white text-slate-900 shadow-sm'
                                    : 'border-transparent text-slate-700 hover:text-slate-900'
                            }`}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div
                        className="h-64 animate-pulse rounded-lg bg-slate-200"
                        aria-busy="true"
                        aria-label="Loading table"
                    />
                ) : error ? (
                    <div role="alert" className="flex flex-col items-start gap-3 py-6 sm:flex-row sm:items-center">
                        <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
                        <p className="text-sm text-slate-700">Couldn’t load the tables. Try again.</p>
                        <Button variant="outline" size="sm" onClick={loadReports}>
                            <RotateCw className="h-4 w-4" />
                            Retry
                        </Button>
                    </div>
                ) : (
                    <DataTable
                        key={current.id}
                        columns={current.columns}
                        rowKey={current.rowKey}
                        searchPlaceholder={current.searchPlaceholder}
                        emptyMessage={current.emptyMessage}
                        {...current.table.tableProps}
                    />
                )}
            </CardContent>
            <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
        </Card>
    );
};

export default Reports;
